import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { projectId } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { EnvironmentBadge, SidebarBrand, SidebarNav } from "./Sidebar";
import { ThemeToggle } from "./ThemeToggle";

/**
 * A casca do painel.
 *
 * A partir de `lg` a barra lateral é fixa; abaixo disso ela vira uma gaveta
 * aberta por um botão no topo. Não é `Sheet` do Radix de propósito: a gaveta
 * precisa fechar sozinha ao navegar, e o controle direto do estado deixa isso
 * explícito em vez de depender do foco.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const [location] = useLocation();
  // Trocar de tela fecha a gaveta. Sem isso, o painel aparece por baixo dela e
  // a pessoa precisa de um segundo toque só para ver o que pediu. A gaveta
  // guarda a rota em que foi aberta, e só fica aberta nela.
  const [drawerOpenAt, setDrawerOpenAt] = useState<string | null>(null);
  const drawerOpen = drawerOpenAt === location;
  const setDrawerOpen = (open: boolean) => setDrawerOpenAt(open ? location : null);

  // Com a gaveta aberta, a página de trás não pode rolar: no celular o dedo
  // rola o conteúdo escondido e a gaveta parece travada.
  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  return (
    <div className="min-h-viewport bg-background">
      {/* Barra lateral fixa, a partir de lg */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col gap-4 border-r border-border bg-card p-4 lg:flex">
        <SidebarBrand />
        <EnvironmentBadge />
        <SidebarNav />
        <div className="mt-auto flex flex-col gap-2">
          <Separator />
          <p className="truncate text-caption text-muted-foreground" title={user?.email ?? ""}>
            {user?.email}
          </p>
          <p className="truncate text-caption text-muted-foreground" title={projectId}>
            {projectId}
          </p>
          <Button variant="ghost" size="sm" className="justify-start" onClick={() => void signOut()}>
            <LogOut /> Sair
          </Button>
        </div>
      </aside>

      {/* Gaveta, abaixo de lg */}
      {drawerOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Fechar menu"
            className="absolute inset-0 bg-overlay"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col gap-4 border-r border-border bg-card p-4">
            <div className="flex items-start justify-between gap-2">
              <SidebarBrand />
              <Button variant="ghost" size="icon" aria-label="Fechar menu" onClick={() => setDrawerOpen(false)}>
                <X />
              </Button>
            </div>
            <EnvironmentBadge />
            <SidebarNav onNavigate={() => setDrawerOpen(false)} />
            <div className="mt-auto flex flex-col gap-2">
              <Separator />
              <p className="truncate text-caption text-muted-foreground">{user?.email}</p>
              <Button variant="ghost" size="sm" className="justify-start" onClick={() => void signOut()}>
                <LogOut /> Sair
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="lg:pl-60">
        {/* Topo compacto, só abaixo de lg */}
        <header className="sticky top-0 z-20 flex min-h-14 items-center gap-2 border-b border-border bg-background/95 px-4 backdrop-blur lg:hidden">
          <Button variant="ghost" size="icon" aria-label="Abrir menu" onClick={() => setDrawerOpen(true)}>
            <Menu />
          </Button>
          <div className="min-w-0 flex-1">
            <SidebarBrand />
          </div>
          <ThemeToggle />
        </header>

        <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8">{children}</main>
      </div>
    </div>
  );
}
